import { useState } from 'react';
import {
  Search, Filter, Microscope, Plus, X, ChevronDown, ChevronUp,
  Clock, CheckCircle2, XCircle, Loader2, DollarSign,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson } from '../lib/api';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface ResearchViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

interface ResearchTask {
  id: string;
  entityType: string;
  entityId: string;
  entityName: string;
  taskType: string;
  status: 'queued' | 'running' | 'complete' | 'failed';
  resultSummary?: string;
  resultFull?: string;
  cost?: number;
  createdAt: string;
  completedAt?: string;
}

interface ResearchTasksResponse {
  tasks: ResearchTask[];
  total: number;
}

const statusColors: Record<string, string> = {
  queued: 'gray',
  running: 'blue',
  complete: 'green',
  failed: 'red',
};

const statusIcons: Record<string, typeof Clock> = {
  queued: Clock,
  running: Loader2,
  complete: CheckCircle2,
  failed: XCircle,
};

const taskTypes = [
  'web_search',
  'social_media',
  'company_profile',
  'news_mentions',
  'linkedin_profile',
];

export function ResearchView({ onNavigate }: ResearchViewProps) {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newEntitySearch, setNewEntitySearch] = useState('');
  const [newTaskType, setNewTaskType] = useState(taskTypes[0]);
  const [selectedEntity, setSelectedEntity] = useState<{ id: string; name: string; type: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['research-tasks', statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      return fetchJson<ResearchTasksResponse>(`/api/research/tasks${qs ? `?${qs}` : ''}`);
    },
  });

  const { data: entitySearch } = useQuery({
    queryKey: ['research-entity-search', newEntitySearch],
    queryFn: () =>
      fetchJson<{ results: { id: string; name: string; type: string }[] }>(
        `/api/search?q=${encodeURIComponent(newEntitySearch)}&types=contact,organization&limit=8`,
      ),
    enabled: newEntitySearch.length >= 2,
  });

  const createMutation = useMutation({
    mutationFn: (data: { entityType: string; entityId: string; taskType: string }) =>
      postJson<ResearchTask>('/api/research/tasks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['research-tasks'] });
      setShowNewModal(false);
      setSelectedEntity(null);
      setNewEntitySearch('');
    },
  });

  const tasks = data?.tasks ?? [];

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Research</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} research tasks` : 'Automated research task queue'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowNewModal(true)}>
          <Plus className="h-4 w-4" />
          New Research
        </button>
      </div>

      {/* Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>
        {showFilters && (
          <div className="mt-3">
            <label className="label mb-1">Status</label>
            <select
              className="input py-1.5"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="queued">Queued</option>
              <option value="running">Running</option>
              <option value="complete">Complete</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        )}
      </div>

      {/* Task Table */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load research tasks.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="h-5 w-5 animate-pulse rounded-full bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-40 animate-pulse rounded bg-surface-200" />
                </div>
              </div>
            ))}
          </div>
        ) : tasks.length > 0 ? (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Status</th>
                  <th className="table-header">Entity</th>
                  <th className="table-header">Task Type</th>
                  <th className="table-header">Summary</th>
                  <th className="table-header">Cost</th>
                  <th className="table-header">Date</th>
                  <th className="table-header w-10"></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const StatusIcon = statusIcons[task.status] ?? Clock;
                  const expanded = expandedId === task.id;

                  return (
                    <>
                      <tr
                        key={task.id}
                        className="table-row-hover cursor-pointer"
                        onClick={() => setExpandedId(expanded ? null : task.id)}
                      >
                        <td className="table-cell">
                          <div className="flex items-center gap-1.5">
                            <StatusIcon className={cn(
                              'h-4 w-4',
                              task.status === 'running' && 'animate-spin text-blue-500',
                              task.status === 'complete' && 'text-green-500',
                              task.status === 'failed' && 'text-red-500',
                              task.status === 'queued' && 'text-slate-400',
                            )} />
                            <Badge color={statusColors[task.status]}>{task.status}</Badge>
                          </div>
                        </td>
                        <td className="table-cell">
                          <div>
                            <span className="text-sm font-medium text-slate-700">{task.entityName}</span>
                            <Badge color="gray" size="sm" className="ml-1.5">{task.entityType}</Badge>
                          </div>
                        </td>
                        <td className="table-cell text-sm text-slate-600">
                          {task.taskType.replace('_', ' ')}
                        </td>
                        <td className="table-cell text-sm text-slate-500 max-w-xs truncate">
                          {task.resultSummary ?? '--'}
                        </td>
                        <td className="table-cell text-sm text-slate-500">
                          {task.cost != null ? `$${task.cost.toFixed(3)}` : '--'}
                        </td>
                        <td className="table-cell text-xs text-slate-500">
                          {new Date(task.createdAt).toLocaleDateString()}
                        </td>
                        <td className="table-cell">
                          {expanded ? (
                            <ChevronUp className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          )}
                        </td>
                      </tr>
                      {expanded && task.resultFull && (
                        <tr key={`${task.id}-detail`}>
                          <td colSpan={7} className="bg-surface-50 px-6 py-4">
                            <div className="whitespace-pre-wrap text-sm text-slate-600 leading-relaxed">
                              {task.resultFull}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={Microscope}
            title="No research tasks"
            description="Start a research task to automatically gather information about your contacts."
            actionLabel="New Research"
            onAction={() => setShowNewModal(true)}
          />
        )}
      </div>

      {/* New Research Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-md p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">New Research Task</h2>
              <button onClick={() => setShowNewModal(false)} className="btn-ghost rounded-full p-1">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Entity Search */}
              <div>
                <label className="label mb-1">Entity</label>
                {selectedEntity ? (
                  <div className="flex items-center gap-2 rounded-md border border-surface-200 px-3 py-2">
                    <Badge color={selectedEntity.type === 'contact' ? 'blue' : 'purple'} size="sm">
                      {selectedEntity.type}
                    </Badge>
                    <span className="flex-1 text-sm text-slate-700">{selectedEntity.name}</span>
                    <button onClick={() => setSelectedEntity(null)} className="text-slate-400 hover:text-slate-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={newEntitySearch}
                      onChange={(e) => setNewEntitySearch(e.target.value)}
                      placeholder="Search contact or organization..."
                      className="input w-full"
                    />
                    {entitySearch && entitySearch.results.length > 0 && newEntitySearch.length >= 2 && (
                      <div className="absolute z-10 mt-1 w-full rounded-lg border border-surface-200 bg-white py-1 shadow-lg">
                        {entitySearch.results.map((r) => (
                          <button
                            key={`${r.type}-${r.id}`}
                            onClick={() => { setSelectedEntity(r); setNewEntitySearch(''); }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-50"
                          >
                            <Badge color={r.type === 'contact' ? 'blue' : 'purple'} size="sm">{r.type}</Badge>
                            <span>{r.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Task Type */}
              <div>
                <label className="label mb-1">Task Type</label>
                <select
                  className="input w-full"
                  value={newTaskType}
                  onChange={(e) => setNewTaskType(e.target.value)}
                >
                  {taskTypes.map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary" onClick={() => setShowNewModal(false)}>Cancel</button>
                <button
                  className="btn-primary"
                  disabled={!selectedEntity || createMutation.isPending}
                  onClick={() => {
                    if (selectedEntity) {
                      createMutation.mutate({
                        entityType: selectedEntity.type,
                        entityId: selectedEntity.id,
                        taskType: newTaskType,
                      });
                    }
                  }}
                >
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Microscope className="h-4 w-4" />
                  )}
                  Start Research
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
